import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { db } from '@/plugins/firebase'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { useAuthStore } from './auth'
import { useSpeechSynthesis } from '@vueuse/core'
import { useAppStore } from './app'
import $snackBar from '@/services/snackBar'

export const useSpeakStore = defineStore('speak', () => {
	const appStore = useAppStore()

	const voices = ref([])
	const pitch = ref(1)
	const rate = ref(1)
	const speechSynthesis = window.speechSynthesis
	const currentVoice = ref(null)
	const userRef = ref(null)
	const speakingText = ref('')
	const currentLanguage = ref('en')
	const withShuffle = ref(true)
	const isRestoringSettings = ref(false)
	const pendingSavedVoice = ref(null)
	const isSafariVoiceInitialized = ref(false)

	const voicesAccordingByLang = computed(() => voices.value.filter((voice) => voice.lang.includes(currentLanguage.value)))
	const speech = useSpeechSynthesis(speakingText, {
		voice: computed(() => voicesAccordingByLang.value[currentVoice.value?.index]),
		rate,
		pitch
	})

	const getPreparedVoices = computed(() =>
		voicesAccordingByLang.value.map((voice, index) => ({
			...voice,
			label: `${voice.name} (${voice.lang})`,
			index
		}))
	)
	const isSpeaking = computed(() => speech.isPlaying.value)

	const speak = (textToSpeak) => {
		if (isSpeaking.value && textToSpeak === speakingText.value) {
			stopSpeak()
			return
		}
		if (speech.status.value === 'pause') {
			speechSynthesis.resume()
		} else {
			speakingText.value = textToSpeak
			speech.speak()
		}
	}

	const pause = () => {
		speechSynthesis.pause()
	}

	const stopSpeak = () => {
		speech.stop()
	}

	const resolveCurrentVoice = () => {
		const prepared = getPreparedVoices.value
		if (!prepared.length) return

		const saved = pendingSavedVoice.value
		if (saved?.name) {
			currentVoice.value =
				prepared.find((voice) => voice.name === saved.name && voice.lang === saved.lang) ?? prepared[0]
		} else if (!currentVoice.value) {
			const safariCondition = (voice) => voice.label.includes('Samantha') || voice.label.includes('Саманта')
			if (appStore.userBrowser === 'safari') {
				currentVoice.value = prepared.find(safariCondition) ?? prepared.at(-1)
			} else {
				currentVoice.value = prepared.at(-1) ?? prepared[0]
			}
		}
		pendingSavedVoice.value = null
	}

	const initUserSettings = async () => {
		const authStore = useAuthStore()
		userRef.value = doc(db, 'settings', authStore.user.email)
		const userSnap = await getDoc(userRef.value)
		const { rate: userRate, currentVoice: userCurrentVoice, pitch: userPitch, lang, withShuffle:userShuffleSetting } = userSnap.data()
		pendingSavedVoice.value = userCurrentVoice
		rate.value = userRate
		pitch.value = userPitch
		withShuffle.value = userShuffleSetting
		isRestoringSettings.value = true
		currentLanguage.value = lang
		isRestoringSettings.value = false
		resolveCurrentVoice()
	}

	const saveUserSettings = async () => {
		try {
			await updateDoc(userRef.value, {
				pitch: pitch.value,
				rate: rate.value,
				currentVoice: currentVoice.value,
				lang: currentLanguage.value,
				withShuffle: withShuffle.value
			})
			$snackBar.success('Saved!')
		} catch (error) {
			$snackBar.error('Some error')
			console.warn(error)
		}
	}

	const loadVoices = () => {
		const availableVoices = speechSynthesis.getVoices()
		if (!availableVoices.length) return

		voices.value = availableVoices
		resolveCurrentVoice()

		if (appStore.userBrowser === 'safari' && !isSafariVoiceInitialized.value) {
			isSafariVoiceInitialized.value = true
			setTimeout(() => speak(' '), 100)
		}
	}

	const setSpeechSynthesis = () => {
		loadVoices()
		speechSynthesis.onvoiceschanged = loadVoices
	}

	watch(
		currentLanguage,
		() => {
			if (isRestoringSettings.value) return
			if (getPreparedVoices.value.length) {
				currentVoice.value = getPreparedVoices.value[0]
			}
		},
		{ flush: 'sync' }
	)

	return {
		voices,
		pitch,
		rate,
		currentVoice,
		getPreparedVoices,
		withShuffle,
		initUserSettings,
		saveUserSettings,
		setSpeechSynthesis,
		speak,
		pause,
		stopSpeak,
		isSpeaking,
		speakingText,
		currentLanguage
	}
})
